import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Filter, Minus, Search } from 'lucide-react';

const TABLE_EMPTY_CLS =
  'px-6 py-16 text-center text-sm text-gray-500';

// Custom-drawn selection checkbox: the native control reads as a bare grey
// square against the dark table. Keeps a real <input> for keyboard/AT; the
// peer-* utilities drive the drawn box, check, and indeterminate dash.
function SelectionCheckbox({ checked, indeterminate = false, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <span className="relative inline-flex h-4 w-4 items-center justify-center align-middle">
      <input
        ref={ref}
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={onChange}
        className="peer h-4 w-4 cursor-pointer appearance-none rounded-[4px] border border-white/20 bg-white/[0.04] transition-colors hover:border-white/40 checked:border-[#B0FC38] checked:bg-[#B0FC38] indeterminate:border-[#B0FC38] indeterminate:bg-[#B0FC38]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-[#0a0a0f]"
      />
      <Check className="pointer-events-none absolute h-3 w-3 text-[#0a0a0f] opacity-0 peer-checked:opacity-100" strokeWidth={3} />
      <Minus className="pointer-events-none absolute h-3 w-3 text-[#0a0a0f] opacity-0 peer-indeterminate:opacity-100" strokeWidth={3} />
    </span>
  );
}

export default function DataTable({
  columns,
  rows,
  searchableKeys = [],
  filters = [],
  defaultSort,
  // Optional row selection (admin multiselect): parent owns the state.
  // selectable enables the checkbox column; selectedIds is an array of
  // row[rowKey] values; onToggleRow/onToggleAll mutate it.
  selectable = false,
  selectedIds = [],
  onToggleRow,
  onToggleAll,
  onRowClick,
  loading,
  emptyMessage = 'No records yet.',
  toolbarExtra,
  rowKey = 'id',
}) {
  const [query, setQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [sortKey, setSortKey] = useState(defaultSort?.key || null);
  const [sortDir, setSortDir] = useState(defaultSort?.direction || 'asc');

  const filtered = useMemo(() => {
    let out = rows || [];
    const q = query.trim().toLowerCase();
    if (q && searchableKeys.length) {
      out = out.filter((row) =>
        searchableKeys.some((k) => {
          const v = row[k];
          return v != null && String(v).toLowerCase().includes(q);
        })
      );
    }
    for (const [key, value] of Object.entries(activeFilters)) {
      if (value === null || value === undefined || value === '') continue;
      out = out.filter((row) => String(row[key] ?? '') === String(value));
    }
    if (sortKey) {
      out = [...out].sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (av == null && bv == null) return 0;
        if (av == null) return sortDir === 'asc' ? -1 : 1;
        if (bv == null) return sortDir === 'asc' ? 1 : -1;
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortDir === 'asc' ? av - bv : bv - av;
        }
        const cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, query, activeFilters, sortKey, sortDir, searchableKeys]);

  const toggleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey !== col.key) {
      setSortKey(col.key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortKey(null);
      setSortDir('asc');
    }
  };

  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-4 md:px-5 py-3.5 border-b border-white/[0.05] bg-white/[0.01]">
        {(searchableKeys.length > 0) && (
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 transition-all"
            />
          </div>
        )}
        {filters.map((f) => (
          <div key={f.key} className="relative">
            {/* A filter may supply its own control (e.g. AccountPicker for
                rosters far too large for a <select>). It receives the current
                value and a setter; empty string clears the filter. */}
            {typeof f.control === 'function' ? (
              f.control(activeFilters[f.key] ?? '', (v) =>
                setActiveFilters((prev) => ({ ...prev, [f.key]: v || undefined }))
              )
            ) : (
            <><Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-600 pointer-events-none" />
            <select
              value={activeFilters[f.key] ?? ''}
              onChange={(e) =>
                setActiveFilters((prev) => ({ ...prev, [f.key]: e.target.value || undefined }))
              }
              className="pl-8 pr-8 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 transition-all appearance-none"
            >
              <option value="">All {f.label}</option>
              {f.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select></>
            )}
          </div>
        ))}
        <div className="flex-1" />
        {toolbarExtra}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.015] border-b border-white/[0.05]">
            <tr>
              {selectable && (
                <th className="w-10 px-3 py-3">
                  <SelectionCheckbox
                    label="Select all rows"
                    checked={filtered.length > 0 && filtered.every((r) => selectedIds.includes(r[rowKey]))}
                    indeterminate={filtered.some((r) => selectedIds.includes(r[rowKey]))}
                    onChange={() => onToggleAll?.(filtered.map((r) => r[rowKey]))}
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 md:px-5 py-3 text-left text-[10px] font-semibold text-gray-500 tracking-wider uppercase whitespace-nowrap ${col.sortable ? 'cursor-pointer select-none hover:text-gray-300 transition-colors' : ''} ${col.align === 'right' ? 'text-right' : ''}`}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={() => toggleSort(col)}
                >
                  <span className={`inline-flex items-center gap-1.5 ${col.align === 'right' ? 'justify-end w-full' : ''}`}>
                    {col.header}
                    {col.sortable && (
                      sortKey === col.key ? (
                        sortDir === 'asc' ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-40" />
                      )
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className={TABLE_EMPTY_CLS}>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-[#B0FC38]/60 border-t-transparent rounded-full animate-spin" />
                    Loading…
                  </span>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className={TABLE_EMPTY_CLS}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr
                  key={row[rowKey]}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`border-b border-white/[0.03] last:border-b-0 transition-colors ${onRowClick ? 'cursor-pointer hover:bg-white/[0.02]' : ''}`}
                >
                  {selectable && (
                    <td className="w-10 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <SelectionCheckbox
                        label="Select row"
                        checked={selectedIds.includes(row[rowKey])}
                        onChange={() => onToggleRow?.(row[rowKey])}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 md:px-5 py-3.5 text-gray-300 ${col.align === 'right' ? 'text-right' : ''}`}
                    >
                      {col.render ? col.render(row) : row[col.key] ?? <span className="text-gray-600">—</span>}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
